import { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Spinner,
  Textarea,
} from '@design-system';
import { format } from 'date-fns';
import { CircleCheck, CircleX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { quickEditFieldLabel } from '@shared';
import { ClientHomeTopBar } from '@client/components/ClientHomeTopBar';
import { useClientSignOut } from '@client/components/hooks/use-sign-out';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import { getQuickEditFieldError } from '@client/components/quick-edit-field-validation';
import {
  formatQuickEditFieldValue,
  parseQuickEditFieldValue,
  QuickEditFieldInput,
} from '@client/components/quick-edit-field-input';
import { useIntakeChangeRequestReview } from './use-intake-change-request-review';

export function IntakeChangeRequestReviewPage({ clientId }: { clientId: string }) {
  const review = useIntakeChangeRequestReview(clientId);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  if (review.isLoading) {
    return (
      <ClientReviewPageLayout>
        <Spinner />
      </ClientReviewPageLayout>
    );
  }
  const request = review.request;
  if (
    review.decision === 'approved' ||
    request?.status === 'approved' ||
    request?.status === 'applied'
  ) {
    return (
      <ClientReviewPageLayout>
        <ApprovalSuccess />
      </ClientReviewPageLayout>
    );
  }
  if (review.decision === 'rejected') {
    return (
      <ClientReviewPageLayout>
        <RejectionSuccess />
      </ClientReviewPageLayout>
    );
  }
  if (request?.status === 'expired') {
    return (
      <ClientReviewPageLayout>
        <DecisionMessage>This request expired and no longer needs a decision.</DecisionMessage>
      </ClientReviewPageLayout>
    );
  }
  if (!request || request.status !== 'pending') {
    return (
      <ClientReviewPageLayout>
        <DecisionMessage>This request changed. Return to your plan.</DecisionMessage>
      </ClientReviewPageLayout>
    );
  }

  const visibleChanges = request.changes.filter(
    change => change.questionRef !== 'clientHasDifferentLtcZipCode',
  );
  const fieldErrors = Object.fromEntries(
    visibleChanges.map(change => {
      const value = reviewValue(change, review.values);
      return [
        change.questionRef,
        getQuickEditFieldError(
          change.questionRef,
          formatQuickEditFieldValue(change.questionRef, value),
        ),
      ];
    }),
  );
  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  return (
    <ClientReviewPageLayout>
      <Card>
        <CardHeader className="gap-3">
          <CardTitle>Review requested intake changes</CardTitle>
          <CardDescription className="space-y-1 text-sm leading-6 text-gray-600">
            <p>
              {request.requestDescription ??
                'Your advisor is requesting to update some of your intake answers.'}
            </p>
            <p>
              Requested {format(new Date(request.createdDateTime), "MMMM d, yyyy 'at' h:mm a")}.
            </p>
            <p>
              Review each answer below. You can edit the proposed answers before approving.
              Approving applies the changes and updates your plan. Rejecting leaves your current
              answers and plan unchanged.
            </p>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {review.changed && <p role="alert">This request changed. Review the latest request.</p>}
          {review.approvalError && (
            <p role="alert">
              {review.approvalError instanceof Error
                ? review.approvalError.message
                : 'The request could not be updated. Try again.'}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[28%]" />
                <col className="w-[38%]" />
              </colgroup>
              <thead>
                <tr className="border-b">
                  <th className="p-3">Question</th>
                  <th className="p-3">Previous answer</th>
                  <th className="p-3">New answer</th>
                </tr>
              </thead>
              <tbody>
                {visibleChanges.map(change => {
                  const value = reviewValue(change, review.values);
                  const fieldError = fieldErrors[change.questionRef];
                  return (
                    <tr
                      className="border-b"
                      key={change.questionRef}
                    >
                      <th className="p-3 font-medium">{quickEditFieldLabel(change.questionRef)}</th>
                      <td className="p-3">{displayValue(change.previousValue)}</td>
                      <td className="p-3">
                        <Label
                          className="sr-only"
                          htmlFor={`intake-change-${change.questionRef}`}
                        >
                          New answer for {quickEditFieldLabel(change.questionRef)}
                        </Label>
                        <QuickEditFieldInput
                          className="mt-2 w-full min-w-0 max-w-sm"
                          fieldName={change.questionRef}
                          id={`intake-change-${change.questionRef}`}
                          value={formatQuickEditFieldValue(change.questionRef, value)}
                          aria-invalid={touched[change.questionRef] && !!fieldError}
                          aria-describedby={
                            touched[change.questionRef] && fieldError
                              ? `intake-change-${change.questionRef}-error`
                              : undefined
                          }
                          onValueChange={nextValue =>
                            review.changeValue(
                              change.questionRef,
                              parseQuickEditFieldValue(change.questionRef, nextValue),
                            )
                          }
                          onBlur={() =>
                            setTouched(current => ({ ...current, [change.questionRef]: true }))
                          }
                        />
                        {touched[change.questionRef] && fieldError && (
                          <p
                            id={`intake-change-${change.questionRef}-error`}
                            className="mt-1 text-xs text-red-500"
                          >
                            {fieldError}
                          </p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              disabled={hasFieldErrors || review.isApproving || review.isRejecting}
              onClick={() => void review.approveRequest()}
            >
              {review.isApproving ? 'Applying changes…' : 'Approve changes'}
            </Button>
            <Button
              disabled={review.isApproving || review.isRejecting}
              variant="outline"
              onClick={() => review.setRejectOpen(true)}
            >
              Reject changes
            </Button>
          </div>
        </CardContent>
      </Card>
      <AlertDialog
        open={review.rejectOpen}
        onOpenChange={review.setRejectOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this request?</AlertDialogTitle>
            <AlertDialogDescription>
              Tell your advisor why these changes should not be applied.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {review.rejectionError && (
            <p
              role="alert"
              className="text-sm text-red-700"
            >
              {review.rejectionError instanceof Error
                ? review.rejectionError.message
                : 'The rejection could not be saved. Try again.'}
            </p>
          )}
          <div className="space-y-2">
            <Label htmlFor="intake-change-rejection-reason">Reason for rejecting</Label>
            <Textarea
              id="intake-change-rejection-reason"
              value={review.rejectionReason}
              onChange={event => review.setRejectionReason(event.target.value)}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={review.isRejecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!review.rejectionReason.trim() || review.isRejecting}
              onClick={event => {
                event.preventDefault();
                void review.rejectRequest();
              }}
            >
              {review.isRejecting ? 'Rejecting…' : 'Reject request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClientReviewPageLayout>
  );
}

function ApprovalSuccess() {
  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-6 p-6 sm:p-8">
        <div className="bg-primary-50 flex h-12 w-12 items-center justify-center rounded-full">
          <CircleCheck
            className="text-primary h-7 w-7"
            aria-hidden="true"
          />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-gray-950">Approved. Your plan is updating.</h1>
          <p className="leading-6 text-gray-600">
            You can return to your plan while the update finishes.
          </p>
        </div>
        <Button asChild>
          <Link to="/client/home">Back to home</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function RejectionSuccess() {
  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-6 p-6 sm:p-8">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <CircleX
            className="h-7 w-7 text-gray-600"
            aria-hidden="true"
          />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-gray-950">This request was rejected.</h1>
          <p className="leading-6 text-gray-600">Your answers and plan did not change.</p>
        </div>
        <Button asChild>
          <Link to="/client/home">Back to home</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function ClientReviewPageLayout({ children }: { children: React.ReactNode }) {
  const { clerkEmail } = useClerkClientAuth();
  const handleSignOut = useClientSignOut();

  return (
    <>
      <ClientHomeTopBar
        handleClickSignOut={handleSignOut}
        clientEmail={clerkEmail ?? ''}
      />
      <main className="mx-auto w-full max-w-3xl px-4 py-12 sm:px-6">{children}</main>
    </>
  );
}

function DecisionMessage({ children }: { children: React.ReactNode }) {
  return (
    <Card>
      <CardContent className="pt-6">{children}</CardContent>
    </Card>
  );
}

function selectedValue(change: IntakeChangeRequestChange): string | number | boolean | null {
  const hasClientValue = change.clientValueSet ?? change.clientValue !== null;
  return hasClientValue ? change.clientValue : change.advisorValue;
}

function reviewValue(
  change: IntakeChangeRequestChange,
  values: Record<string, string | number | boolean | null>,
): string | number | boolean | null {
  return Object.prototype.hasOwnProperty.call(values, change.questionRef)
    ? values[change.questionRef]
    : selectedValue(change);
}

function displayValue(value: string | number | boolean | null): string {
  if (value == null || value === '') return 'Not answered';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
